import { captureClientException } from '@dinewise/logger/client';
import { hasSubUnits, toDecimal, toSnapshot, toUnits } from 'dinero.js';
import {
  currencyCodeSchema,
  getCurrencyMinorUnitExponent,
  type CurrencyCode,
  type Money,
  type MoneyJson,
  moneyFromJson,
  moneyJsonSchema,
} from './money.ts';

type CurrencyDisplay = 'symbol' | 'narrowSymbol' | 'code' | 'name';

type CurrencyFormatterOptions = {
  locale?: string;
  currencyCode?: string;
  precision?: number;
  minimumFractionDigits?: number;
  currencyDisplay?: CurrencyDisplay;
};

type FormatMoneyOptions = {
  locale?: string;
  precision?: number;
  currencyDisplay?: CurrencyDisplay;
};

const formatters = new Map<string, Intl.NumberFormat>();

const parseMoney = (amount: number, currencyCode: CurrencyCode): Money => {
  const parsedCurrencyCode = currencyCodeSchema.parse(currencyCode);

  if (!Number.isFinite(amount)) {
    throw new RangeError('Money amounts must use finite integer minor units');
  }

  const roundedAmount = Math.round(amount);

  if (roundedAmount !== amount) {
    captureClientException({
      error: new Error('Money amount coerced to integer minor units'),
      properties: {
        amount,
        roundedAmount,
        currencyCode: parsedCurrencyCode,
      },
    });
  }

  return moneyFromJson({ amount: roundedAmount, currencyCode: parsedCurrencyCode });
};

const formatDinero = (formatter: Intl.NumberFormat, value: Money) => {
  const snapshot = toSnapshot(value);

  if (snapshot.currency.base === 10) {
    return toDecimal(value, decimal => formatter.format(Number(decimal.value)));
  }

  const [majorUnits = 0, minorUnits = 0] = toUnits(value);
  const base = snapshot.currency.base;

  if (typeof base !== 'number') {
    throw new Error(`Currency ${snapshot.currency.code} has multiple subdivision bases`);
  }

  return formatter.format(majorUnits + minorUnits / base);
};

export const createCurrencyFormatter = ({
  locale = 'en-US',
  currencyCode = 'USD',
  precision,
  minimumFractionDigits,
  currencyDisplay,
}: CurrencyFormatterOptions = {}) => {
  const parsedCurrencyCode = currencyCodeSchema.parse(currencyCode);
  const resolvedPrecision = precision ?? getCurrencyMinorUnitExponent(parsedCurrencyCode);
  const minimumFractionDigitsKey =
    minimumFractionDigits === undefined ? '' : `:min${minimumFractionDigits}`;
  const cacheKey = `${locale}:${parsedCurrencyCode}:${resolvedPrecision}:${currencyDisplay ?? 'symbol'}${minimumFractionDigitsKey}`;
  const existingFormatter = formatters.get(cacheKey);

  if (existingFormatter) {
    return existingFormatter;
  }

  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: parsedCurrencyCode,
    maximumFractionDigits: resolvedPrecision,
    useGrouping: 'always',
  };
  if (minimumFractionDigits !== undefined) {
    formatOptions.minimumFractionDigits = minimumFractionDigits;
  }
  if (currencyDisplay) {
    formatOptions.currencyDisplay = currencyDisplay;
  }
  const formatter = new Intl.NumberFormat(locale, formatOptions);
  formatters.set(cacheKey, formatter);

  return formatter;
};

export const getCurrencySymbol = (
  currencyCode: CurrencyCode,
  locale = 'en-US',
  currencyDisplay?: CurrencyDisplay,
): string =>
  createCurrencyFormatter({ locale, currencyCode, currencyDisplay })
    .formatToParts(0)
    .find(part => part.type === 'currency')?.value ?? currencyCode;

export const formatCurrency = (formatter: Intl.NumberFormat, amount: number) => {
  const currencyCode = currencyCodeSchema.parse(formatter.resolvedOptions().currency);
  const value = parseMoney(amount, currencyCode);

  return formatDinero(formatter, value);
};

export const formatMoney = (
  amountInMinorUnits: number,
  currencyCode: CurrencyCode,
  { locale = 'en-US', precision, currencyDisplay }: FormatMoneyOptions = {},
) => {
  const formatter = createCurrencyFormatter({
    locale,
    currencyCode,
    precision,
    currencyDisplay,
  });
  const value = parseMoney(amountInMinorUnits, currencyCode);

  return formatDinero(formatter, value);
};

export const formatMoneyJson = (value: MoneyJson, options?: FormatMoneyOptions) => {
  const parsed = moneyJsonSchema.parse(value);

  return formatMoney(parsed.amount, parsed.currencyCode, options);
};

/** @deprecated Use `createCurrencyFormatter({ currencyCode, precision: 0 })` or `formatMoney(x, currencyCode, { precision: 0 })`. Removed in MRKT-1175. */
export const formatterUSDNoDecimals = createCurrencyFormatter({
  currencyCode: 'USD',
  precision: 0,
});

/** @deprecated Use `createCurrencyFormatter` / `formatMoney` with an explicit currency. Removed in MRKT-1175. */
export const formatterUSD = createCurrencyFormatter({ currencyCode: 'USD' });

/** @deprecated Use `formatMoney(amountInCents, currencyCode)`. Removed in MRKT-1175. */
export const formatUSD = (amountInCents: number) => formatMoney(amountInCents, 'USD');

export const formatMoneyDroppingZeroMinorUnits = (
  amountInMinorUnits: number,
  currencyCode: CurrencyCode,
  locale = 'en-US',
) => {
  const value = parseMoney(amountInMinorUnits, currencyCode);

  const precision = hasSubUnits(value) ? getCurrencyMinorUnitExponent(currencyCode) : 0;
  const formatter = createCurrencyFormatter({
    locale,
    currencyCode,
    precision,
  });

  return formatDinero(formatter, value);
};

/** @deprecated Use `formatMoneyDroppingZeroMinorUnits(amountInCents, currencyCode)`. Removed in MRKT-1175. */
export const formatUSDDroppingZeroCents = (amountInCents: number) =>
  formatMoneyDroppingZeroMinorUnits(amountInCents, 'USD');

const COMPACT_THOUSANDS = 1000;
const COMPACT_MILLIONS = 1_000_000;

const formatUsdCompactWithoutKSuffix = (value: Money, locale: string) => {
  const precision = hasSubUnits(value) ? getCurrencyMinorUnitExponent('USD') : 0;

  return formatDinero(
    createCurrencyFormatter({
      locale,
      currencyCode: 'USD',
      currencyDisplay: 'narrowSymbol',
      precision,
      minimumFractionDigits: precision,
    }),
    value,
  );
};

export const formatMoneyCompact = (
  amountInMinorUnits: number,
  currencyCode: CurrencyCode = 'USD',
  locale = 'en-US',
) => {
  const value = parseMoney(amountInMinorUnits, currencyCode);

  if (currencyCode !== 'USD') {
    return formatMoneyDroppingZeroMinorUnits(amountInMinorUnits, currencyCode, locale);
  }

  const majorUnits = Number(toDecimal(value, decimal => decimal.value));
  const absoluteMajorUnits = Math.abs(majorUnits);

  if (absoluteMajorUnits < COMPACT_THOUSANDS || absoluteMajorUnits >= COMPACT_MILLIONS) {
    return formatUsdCompactWithoutKSuffix(value, locale);
  }

  const thousands = majorUnits / COMPACT_THOUSANDS;
  const formatter = createCurrencyFormatter({
    locale,
    currencyCode: 'USD',
    currencyDisplay: 'narrowSymbol',
    precision: 2,
    minimumFractionDigits: 0,
  });

  return `${formatter.format(thousands)}K`;
};

export const formatMoneyJsonCompact = (value: MoneyJson, locale = 'en-US') => {
  const parsed = moneyJsonSchema.parse(value);

  return formatMoneyCompact(parsed.amount, parsed.currencyCode, locale);
};

const BUDGET_OVERVIEW_WHOLE_DOLLAR_MAX_MAJOR = 10_000;
const BUDGET_OVERVIEW_K_MAX_MAJOR = 999_949;
const BUDGET_OVERVIEW_M_MAX_EXCLUSIVE_MAJOR = 999_950_000;

const roundHalfUpToOneDecimal = (value: number): number => Math.round(value * 10) / 10;

const formatUsdBudgetOverviewScaledSuffix = (
  scaledValue: number,
  suffix: 'K' | 'M' | 'B',
  locale: string,
): string => {
  const rounded = roundHalfUpToOneDecimal(scaledValue);
  const formatter = createCurrencyFormatter({
    locale,
    currencyCode: 'USD',
    currencyDisplay: 'narrowSymbol',
    precision: 1,
    minimumFractionDigits: 0,
  });

  return `${formatter.format(rounded)}${suffix}`;
};

const formatUsdBudgetOverviewCompactAbsoluteMajor = (
  absMajorUnits: number,
  locale: string,
): string => {
  if (absMajorUnits < BUDGET_OVERVIEW_WHOLE_DOLLAR_MAX_MAJOR) {
    const wholeDollars = Math.round(absMajorUnits);
    const formatter = createCurrencyFormatter({
      locale,
      currencyCode: 'USD',
      currencyDisplay: 'narrowSymbol',
      precision: 0,
    });

    return formatter.format(wholeDollars);
  }

  // The tier boundaries are chosen so that rounding never crosses into the next
  // suffix (the K tier caps at 999.9K, the M tier at 999.9M), so each tier formats
  // directly without a promotion check.
  if (absMajorUnits <= BUDGET_OVERVIEW_K_MAX_MAJOR) {
    return formatUsdBudgetOverviewScaledSuffix(absMajorUnits / 1000, 'K', locale);
  }

  if (absMajorUnits < BUDGET_OVERVIEW_M_MAX_EXCLUSIVE_MAJOR) {
    return formatUsdBudgetOverviewScaledSuffix(absMajorUnits / 1_000_000, 'M', locale);
  }

  return formatUsdBudgetOverviewScaledSuffix(absMajorUnits / 1_000_000_000, 'B', locale);
};

/** Compact display for budget overview card tiles (USD K/M/B tiers; other currencies use full format). */
export const formatBudgetOverviewAmountCompact = (
  amountInMinorUnits: number,
  currencyCode: CurrencyCode = 'USD',
  locale = 'en-US',
) => {
  if (currencyCode !== 'USD') {
    return formatMoneyDroppingZeroMinorUnits(amountInMinorUnits, currencyCode, locale);
  }

  const sign = amountInMinorUnits < 0 ? '-' : '';
  const absMajorUnits = Math.abs(amountInMinorUnits) / 100;

  return sign + formatUsdBudgetOverviewCompactAbsoluteMajor(absMajorUnits, locale);
};

export const formatMoneyJsonBudgetOverviewCompact = (value: MoneyJson, locale = 'en-US') => {
  const parsed = moneyJsonSchema.parse(value);

  return formatBudgetOverviewAmountCompact(parsed.amount, parsed.currencyCode, locale);
};

const budgetOverviewCompactUsesScaledSuffix = (compactAmount: string) => {
  const unsigned = compactAmount.startsWith('-') ? compactAmount.slice(1) : compactAmount;

  return unsigned.endsWith('K') || unsigned.endsWith('M') || unsigned.endsWith('B');
};

/**
 * True when the overview tile should expose the exact (cents) amount in a tooltip.
 *
 * Only the abbreviated K/M/B tiers hide meaningful magnitude, so the tooltip is
 * reserved for those. The whole-dollar tier drops at most 99 cents, which is not
 * worth a tooltip (e.g. $102.50 shown as $103 does not open one).
 */
export const shouldShowBudgetOverviewExactTooltip = (value: MoneyJson, locale = 'en-US') => {
  const parsed = moneyJsonSchema.parse(value);
  const compactAmount = formatBudgetOverviewAmountCompact(
    parsed.amount,
    parsed.currencyCode,
    locale,
  );

  return budgetOverviewCompactUsesScaledSuffix(compactAmount);
};
